import 'firebase/firestore'
import { AbstractItem, StreamItem } from '@/types/stream'
import firebaseApp, { firebase } from './app'
import store, { AppState } from '@/store'
import { userName } from '@/types/auth'
import QueryDocumentSnapshot = firebase.firestore.QueryDocumentSnapshot
import DocumentData = firebase.firestore.DocumentData
import CollectionReference = firebase.firestore.CollectionReference
import DocumentReference = firebase.firestore.DocumentReference
import Query = firebase.firestore.Query


const db = firebaseApp.firestore()


interface QueryOptions {
    limit: number;
}


export class PaginatedResponse<C extends Collection<I>, I extends AbstractItem> {
    collection !: C
    docs !: QueryDocumentSnapshot<DocumentData>[]
    queryOptions!: QueryOptions

    constructor(collection: C, docs: QueryDocumentSnapshot<DocumentData>[], queryOptions: QueryOptions) {
        this.collection = collection
        this.docs = docs
        this.queryOptions = queryOptions
    }

    get items(): I[] {
        const items: I[] = []
        for (const doc of this.docs) {
            const item = doc.data() as I
            item.id = doc.id
            items.push(item)
        }
        return items
    }

    get hasAfter(): boolean {
        return this.docs.length >= this.queryOptions.limit
    }

    get afterCursor(): QueryDocumentSnapshot<DocumentData> {
        return this.docs[this.docs.length - 1]
    }

    async listNext() {
        if (this.hasAfter) {
            const response = await this.collection.list(this.afterCursor)
            this.docs.push(...response.docs)
        }
    }
}


/**
 * https://firebase.google.com/docs/reference/js/firebase.firestore
 */
abstract class Collection<I extends AbstractItem> {
    abstract collectionName: string
    limit = 5

    get collection(): CollectionReference<DocumentData> {
        return db.collection(this.collectionName)
    }

    listQuery(startAfter?: QueryDocumentSnapshot<DocumentData>): Query {
        let query = this.collection.orderBy('timestamp', 'desc').limit(this.limit)
        if (startAfter) {
            // https://firebase.google.com/docs/firestore/query-data/query-cursors
            query = query.startAfter(startAfter)
        }
        return query
    }

    async list(startAfter?: QueryDocumentSnapshot<DocumentData>): Promise<PaginatedResponse<this, I>> {
        const data: QueryDocumentSnapshot<DocumentData>[] = []
        const query = this.listQuery(startAfter)
        const querySnapshot = await query.get()
        querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
            data.push(doc)
        })
        return new PaginatedResponse(this, data, {
            limit: this.limit
        })
    }

    async add(item: I): Promise<DocumentReference> {
        // debugger
        // const response = await this.collection.add(item)
        // console.info(response)
        // debugger
        return this.collection.add(item)
    }
}


export class StreamItems extends Collection<StreamItem> {
    collectionName = 'stream-items'

    async addFromInputs(contentValue: string, note: string) {
        const user = (store.state as AppState).user
        if (user === null) {
            throw new Error('Cannot add a stream item if user is not logged in.')
        }
        return this.add({
            id: '',
            title: `A message from ${userName[user]}`,
            author: user,
            seenBy: [],
            reactions: [],
            content: {
                contentType: 'text', // TODO: Get different content types.
                value: contentValue
            },
            timestamp: firebase.firestore.Timestamp.now(),
            note: note
        })
    }
}
